// pages/secure/JobAccessPage.js — Admin & Settings → Job Access (owner, 26 Oct 2026).
//
// "There must be other ways that will not show those menus … maybe we have to build these
// people their own access?" Each office login can be given a job profile (Receptionist,
// Secretary, Bookkeeper, Accounting, Office Manager, Agent, Custom). The profile is a set of
// business areas; the person's menu and the server both stop at those areas. A profile only
// ever takes away: the access level on the employee record still decides money and HR pages.
//
// Managing broker only (routes/jobAccess.js). "See what they see" uses the menu's own
// visibleSections, so the preview is the menu that person will get, not a description of it.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from '../../components/secure/Modal';
import DataList from '../../components/shared/DataList';
import PageHeader, { AboutThisPage } from '../../components/shared/PageHeader';
import { visibleSections } from '../../components/secure/navConfig';

const btn = 'px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900 disabled:opacity-50';
const ghost = 'px-4 py-2 text-sm font-medium bg-white border border-slate-300 rounded-md hover:bg-slate-50';
const link = 'text-blue-600 hover:underline mr-3';

// The areas a person ends up with: the profile's, plus their extra ticks, minus their removed ones.
const effectiveAreas = (profile, extra = [], removed = [], order = []) => {
    const set = new Set(profile ? profile.areas : []);
    extra.forEach((k) => set.add(k));
    removed.forEach((k) => set.delete(k));
    return order.filter((k) => set.has(k));
};

const accessLevel = (p) => (p.is_managing_broker ? 'Managing broker'
    : p.role === 'licensee' ? (p.claims?.isTeamLead ? 'Agent (team lead)' : 'Agent')
        : (p.staff_role || 'Staff login, no access level'));

// The menu this person would see, exactly as DashboardLayout builds it.
const MenuPreview = ({ claims, areas, jobProfile }) => {
    const user = areas ? { ...claims, areas, jobProfile } : claims;
    const sections = visibleSections(user);
    return (
        <div className="grid gap-3 sm:grid-cols-2">
            {sections.map((s) => (
                <div key={s.title} className="border border-slate-200 rounded-md p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{s.title}</div>
                    <ul className="mt-1 text-sm text-slate-800 list-disc ml-5">
                        {s.items.map((it) => <li key={`${it.to}-${it.label}`}>{it.label}</li>)}
                    </ul>
                </div>
            ))}
        </div>
    );
};

const AreaTicks = ({ areas, checked, onToggle, baseline }) => (
    <div className="grid gap-2 sm:grid-cols-2">
        {areas.map((a) => {
            const on = checked.includes(a.key);
            const changed = baseline && on !== baseline.includes(a.key);
            return (
                <label key={a.key} className={`flex gap-2 items-start border rounded-md p-2 cursor-pointer ${on ? 'border-slate-400 bg-slate-50' : 'border-slate-200'}`}>
                    <input type="checkbox" className="mt-1" checked={on} onChange={() => onToggle(a.key)} />
                    <span>
                        <span className="text-sm font-medium text-slate-800">{a.label}</span>
                        {changed && <span className="ml-2 text-xs text-amber-700">{on ? 'added for this person' : 'removed for this person'}</span>}
                        <span className="block text-xs text-slate-500">{a.description}</span>
                    </span>
                </label>
            );
        })}
    </div>
);

const JobAccessPage = () => {
    const api = useApiClient();
    const [params, setParams] = useSearchParams();
    const [cat, setCat] = useState(null);
    const [people, setPeople] = useState([]);
    const [error, setError] = useState('');
    const [flash, setFlash] = useState('');
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null);     // { person, profile_key, checked }
    const [preview, setPreview] = useState(null);     // person
    const [tpl, setTpl] = useState(null);             // { key|null, label, description, areas, builtin }

    const note = (m) => { setFlash(m); setTimeout(() => setFlash(''), 8000); };
    const fail = (e) => setError(e.message || String(e));

    const load = useCallback(() => {
        setError('');
        api.get('/api/job-access/catalogue').then(setCat).catch(fail);
        api.get('/api/job-access/people').then(setPeople).catch(fail);
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const order = useMemo(() => (cat ? cat.areas.map((a) => a.key) : []), [cat]);
    const profileOf = useCallback((key) => (cat ? cat.profiles.find((p) => p.key === key) : null), [cat]);

    const openEdit = useCallback((person) => {
        const prof = profileOf(person.profile_key);
        setEditing({ person, profile_key: person.profile_key || '',
            checked: person.areas || (prof ? prof.areas : []) });
    }, [profileOf]);

    // ?user=ID (from Login Accounts / Employees) opens that person's editor.
    useEffect(() => {
        const id = Number(params.get('user'));
        if (!id || !cat || !people.length) return;
        const p = people.find((x) => x.user_id === id);
        if (p && !p.is_managing_broker) openEdit(p);
        params.delete('user');
        setParams(params, { replace: true });
    }, [params, setParams, cat, people, openEdit]);

    const chooseProfile = (key) => {
        const prof = profileOf(key);
        setEditing((e) => ({ ...e, profile_key: key, checked: prof ? prof.areas.slice() : [] }));
    };
    const toggle = (setter) => (key) => setter((e) => ({ ...e,
        checked: e.checked.includes(key) ? e.checked.filter((k) => k !== key) : order.filter((k) => k === key || e.checked.includes(k)) }));

    const savePerson = async () => {
        const { person, profile_key, checked } = editing;
        setBusy(true); setError('');
        try {
            let body;
            if (!profile_key) body = { profile_key: null };
            else {
                const base = profileOf(profile_key).areas;
                body = { profile_key, extra_areas: checked.filter((k) => !base.includes(k)), removed_areas: base.filter((k) => !checked.includes(k)) };
            }
            const r = await api.put(`/api/job-access/people/${person.user_id}`, body);
            note(r.message); setEditing(null); load();
        } catch (e) { fail(e); } finally { setBusy(false); }
    };

    const saveTemplate = async () => {
        setBusy(true); setError('');
        try {
            const body = { label: tpl.label, description: tpl.description, areas: tpl.areas };
            const r = tpl.key ? await api.put(`/api/job-access/profiles/${tpl.key}`, body) : await api.post('/api/job-access/profiles', body);
            note(r.message); setTpl(null); load();
        } catch (e) { fail(e); } finally { setBusy(false); }
    };
    const resetTemplate = async (p) => {
        if (!window.confirm(`Put the ${p.label} job back to its standard areas? Everyone with this job is signed out so the change applies to them.`)) return;
        try { const r = await api.post(`/api/job-access/profiles/${p.key}/reset`, {}); note(r.message); load(); } catch (e) { fail(e); }
    };
    const deleteTemplate = async (p) => {
        if (!window.confirm(`Remove the ${p.label} job?`)) return;
        try { const r = await api.delete(`/api/job-access/profiles/${p.key}`); note(r.message); load(); } catch (e) { fail(e); }
    };

    const areaLabel = (k) => (cat ? (cat.areas.find((a) => a.key === k) || {}).label || k : k);
    const editProf = editing && profileOf(editing.profile_key);
    const tierMismatch = editing && editProf && editProf.tier && editProf.tier !== 'Agent'
        && editing.person.role === 'admin' && editProf.tier !== editing.person.staff_role;

    return (
        <div>
            <PageHeader title="Job Access"
                description="Give each person the menu their job needs. A job profile only hides things; it never opens a page their access level does not already allow." />
            <AboutThisPage>
                <p>Every part of the portal belongs to an <b>area</b> (front desk, clients, escrow, payroll …). A <b>job profile</b> is a list of areas.
                    Someone with a profile sees only those areas in their menu, and the server refuses the rest. Someone with <b>no profile</b> sees
                    exactly what their access level shows, as before. You, the managing broker, are never restricted.</p>
                <p className="mt-2">The access level on the employee record (Office Manager, Bookkeeper, Staff, Read Only) still decides money and HR
                    pages: ticking Escrow for a Staff login shows nothing new, because escrow needs the Bookkeeper or Office Manager level.</p>
                <p className="mt-2">When you change someone's job access they are signed out, and see the new menu when they sign in again.
                    Changing a job template signs out everyone who has that job.</p>
                {cat && <p className="mt-2"><b>Everyone</b> keeps: {cat.always.description}</p>}
            </AboutThisPage>

            {error && <div className="mt-3 p-3 bg-red-50 text-red-700 rounded-md text-sm">{error}</div>}
            {flash && <div className="mt-3 p-3 bg-green-50 text-green-800 rounded-md text-sm">{flash}</div>}

            <h2 className="mt-6 text-lg font-semibold text-slate-800">People</h2>
            <div className="mt-2 bg-white border border-slate-200 rounded-lg overflow-x-auto">
                <div className="p-3"><DataList rows={people} rowKey="user_id" csvName="job-access" defaultSort={{ key: 'name', dir: 'asc' }} empty="No staff or agent logins yet."
                    filters={[{ key: 'kind', label: 'Type' }, { key: 'job', label: 'Job' }]}
                    columns={[
                        { key: 'name', label: 'Person', render: (p) => <><span className="font-medium">{p.name}</span>
                            <div className="text-xs text-slate-400">{p.username}{p.job_title ? ` · ${p.job_title}` : ''}</div></> },
                        { key: 'kind', label: 'Type' },
                        { key: 'level', label: 'Access level', value: accessLevel },
                        { key: 'job', label: 'Job access', value: (p) => (p.is_managing_broker ? 'everything' : (profileOf(p.profile_key)?.label || (p.profile_key ? p.profile_key : 'as access level'))),
                            render: (p) => (p.is_managing_broker ? <span className="text-slate-400 text-sm">everything (never restricted)</span> : <>
                                <span className={p.profile_key ? 'font-medium' : 'text-slate-500'}>{profileOf(p.profile_key)?.label || p.profile_key || 'as access level'}</span>
                                {(p.extra_areas.length > 0 || p.removed_areas.length > 0) && (
                                    <div className="text-xs text-amber-700">
                                        {p.extra_areas.length > 0 && `+ ${p.extra_areas.map(areaLabel).join(', ')}`}
                                        {p.extra_areas.length > 0 && p.removed_areas.length > 0 && '; '}
                                        {p.removed_areas.length > 0 && `− ${p.removed_areas.map(areaLabel).join(', ')}`}
                                    </div>)}</>) },
                        { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (p) => (
                            <span className="whitespace-nowrap">
                                {!p.is_managing_broker && <button className={link} onClick={() => openEdit(p)}>Change</button>}
                                <button className={link} onClick={() => setPreview(p)}>See what they see</button>
                            </span>) },
                    ]} /></div>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-semibold text-slate-800">Job templates</h2>
                <button className={ghost} onClick={() => setTpl({ key: null, label: '', description: '', areas: [], builtin: false })}>Add a job</button>
            </div>
            <p className="text-sm text-slate-500">Changing a template changes it for everyone who has that job.</p>
            <div className="mt-2 grid gap-3 md:grid-cols-2">
                {(cat ? cat.profiles : []).map((p) => (
                    <div key={p.key} className="bg-white border border-slate-200 rounded-lg p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="font-semibold text-slate-800">{p.label}{p.edited && <span className="ml-2 text-xs text-amber-700">edited</span>}</div>
                            <span>
                                <button className={link} onClick={() => setTpl({ key: p.key, label: p.label, description: p.description || '', areas: p.areas.slice(), builtin: p.builtin })}>Edit</button>
                                {p.builtin && p.edited && <button className={link} onClick={() => resetTemplate(p)}>Reset</button>}
                                {!p.builtin && <button className="text-red-600 hover:underline" onClick={() => deleteTemplate(p)}>Remove</button>}
                            </span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{p.description}</p>
                        {p.tier && <p className="text-xs text-slate-500 mt-1">Meant for the {p.tier} access level.</p>}
                        <p className="text-xs text-slate-500 mt-2">{p.areas.length ? p.areas.map(areaLabel).join(' · ') : 'Only the basics everyone has.'}</p>
                        <Link className="inline-block mt-2 text-sm text-indigo-700 hover:underline" to={`/dashboard/task-automation?tab=duties&job=${encodeURIComponent(p.key)}`}>Duties for this job</Link>
                    </div>
                ))}
            </div>

            {/* ---------------- one person ---------------- */}
            <Modal open={!!editing} onClose={() => setEditing(null)} size="lg"
                title={editing ? `Job access — ${editing.person.name}` : ''}
                footer={<>
                    <button className={ghost} onClick={() => setEditing(null)}>Cancel</button>
                    <button className={btn} disabled={busy} onClick={savePerson}>Save and sign them out</button>
                </>}>
                {editing && cat && <>
                    <p className="text-sm text-slate-600">Access level: <b>{accessLevel(editing.person)}</b></p>
                    <label className="block mt-3 text-sm font-medium text-slate-700">Job
                        <select className="mt-1 block w-full border border-slate-300 rounded-md p-2" value={editing.profile_key} onChange={(e) => chooseProfile(e.target.value)}>
                            <option value="">No job profile — sees what their access level shows</option>
                            {cat.profiles.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                        </select>
                    </label>
                    {editProf && <p className="mt-1 text-xs text-slate-500">{editProf.description}</p>}
                    {tierMismatch && (
                        <p className="mt-2 p-2 bg-amber-50 text-amber-800 rounded text-sm">
                            This job is meant for the {editProf.tier} access level; {editing.person.name} has {editing.person.staff_role || 'no access level'}.
                            Pages that need {editProf.tier} will still be refused. Change the access level on their employee record if they should have them.
                        </p>)}
                    {editing.profile_key && <>
                        <h3 className="mt-4 text-sm font-semibold text-slate-800">Areas for this person</h3>
                        <p className="text-xs text-slate-500 mb-2">Ticked = in their menu. A tick that differs from the job is kept for this person only.</p>
                        <AreaTicks areas={cat.areas} checked={editing.checked} baseline={editProf ? editProf.areas : null} onToggle={toggle(setEditing)} />
                    </>}
                    <h3 className="mt-5 text-sm font-semibold text-slate-800">Their menu will be</h3>
                    <div className="mt-2">
                        <MenuPreview claims={editing.person.claims} areas={editing.profile_key ? effectiveAreas(null, editing.checked, [], order) : null} jobProfile={editing.profile_key} />
                    </div>
                </>}
            </Modal>

            {/* ---------------- preview ---------------- */}
            <Modal open={!!preview} onClose={() => setPreview(null)} size="lg" title={preview ? `What ${preview.name} sees` : ''}
                footer={<button className={ghost} onClick={() => setPreview(null)}>Close</button>}>
                {preview && <>
                    <p className="text-sm text-slate-600 mb-3">
                        {preview.is_managing_broker ? 'The managing broker sees everything.'
                            : preview.profile_key ? `Job: ${profileOf(preview.profile_key)?.label || preview.profile_key}. Access level: ${accessLevel(preview)}.`
                                : `No job profile: the menu of the ${accessLevel(preview)} access level.`}
                    </p>
                    <MenuPreview claims={preview.claims} areas={preview.is_managing_broker ? null : preview.areas} jobProfile={preview.profile_key} />
                </>}
            </Modal>

            {/* ---------------- template ---------------- */}
            <Modal open={!!tpl} onClose={() => setTpl(null)} size="lg" title={tpl?.key ? `Edit the ${tpl.label} job` : 'Add a job'}
                footer={<>
                    <button className={ghost} onClick={() => setTpl(null)}>Cancel</button>
                    <button className={btn} disabled={busy || !tpl?.label} onClick={saveTemplate}>Save</button>
                </>}>
                {tpl && cat && <>
                    <label className="block text-sm font-medium text-slate-700">Name
                        <input className="mt-1 block w-full border border-slate-300 rounded-md p-2" value={tpl.label} maxLength={80}
                            onChange={(e) => setTpl({ ...tpl, label: e.target.value })} />
                    </label>
                    <label className="block mt-3 text-sm font-medium text-slate-700">What the job is for
                        <textarea className="mt-1 block w-full border border-slate-300 rounded-md p-2" rows={2} value={tpl.description} maxLength={500}
                            onChange={(e) => setTpl({ ...tpl, description: e.target.value })} />
                    </label>
                    <h3 className="mt-4 text-sm font-semibold text-slate-800">Areas</h3>
                    <div className="mt-2">
                        <AreaTicks areas={cat.areas} checked={tpl.areas}
                            onToggle={(k) => setTpl((t) => ({ ...t, areas: t.areas.includes(k) ? t.areas.filter((x) => x !== k) : order.filter((x) => x === k || t.areas.includes(x)) }))} />
                    </div>
                </>}
            </Modal>
        </div>
    );
};

export default JobAccessPage;
